/**
 * The catalog's rows: a sortable table (List) or a grid of cards (Tiles), from the list row's fields. Status shows only
 * to `asset.view.draft` holders; a stale asset carries the Stale badge in both views. Empty values read as words.
 */

import { Link } from 'react-router-dom';

import type { AssetCard } from '@/api/types';
import { Badge } from '@/ui/Badge';
import { SortHeader } from '@/ui/SortHeader';
import { formatDate } from '@/ui/format';

import { confirmedText } from '../assetUi';
import { effectiveOrder, effectiveSort } from './catalogParams';
import type { CatalogPageState } from './useCatalogPage';

function tagText(tags: { name: string }[]): string {
  return tags.length > 0 ? tags.map((t) => t.name).join(', ') : 'None';
}

function AssetRow({ asset, drafts }: { asset: AssetCard; drafts: boolean }) {
  return (
    <tr>
      <th scope="row">
        <Link className="cell-link" to={`/assets/${asset.id}`}>{asset.name}</Link>
        {asset.tagline && <div className="sub">{asset.tagline}</div>}
      </th>
      <td><Badge variant="kind" value={asset.kind} /></td>
      {drafts && <td><Badge variant="status" value={asset.status} /></td>}
      <td><Badge variant="maturity" value={asset.maturity} /></td>
      <td>{tagText(asset.industries)}</td>
      <td>
        {confirmedText(asset.last_confirmed_at)}
        {asset.stale && <> <Badge variant="freshness" value="stale" /></>}
      </td>
      <td className="hint">{formatDate(asset.updated_at)}</td>
    </tr>
  );
}

export function CatalogTable({ state, rows }: { state: CatalogPageState; rows: AssetCard[] }) {
  const sort = effectiveSort(state.view);
  const order = effectiveOrder(state.view);
  const header = (label: string, key: string) => <SortHeader label={label} sortKey={key} sort={sort} order={order} onSort={state.onSort} />;
  return (
    <div className="table-scroll">
      <table className="data zebra compact catalog-table" aria-label="Assets">
        <thead>
          <tr>
            {header('Name', 'name')}
            {header('Kind', 'kind')}
            {state.canDrafts && <th scope="col">Status</th>}
            <th scope="col">Maturity</th>
            <th scope="col">Industries</th>
            {header('Confirmed', 'confirmed')}
            {header('Updated', 'updated')}
          </tr>
        </thead>
        <tbody>
          {rows.map((asset) => (
            <AssetRow key={asset.id} asset={asset} drafts={state.canDrafts} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetTile({ asset, drafts }: { asset: AssetCard; drafts: boolean }) {
  return (
    <li className="card catalog-tile">
      <div className="catalog-tile-badges">
        <Badge variant="kind" value={asset.kind} />
        <Badge variant="maturity" value={asset.maturity} />
        {drafts && asset.status !== 'published' && <Badge variant="status" value={asset.status} />}
        {asset.stale && <Badge variant="freshness" value="stale" />}
      </div>
      <Link className="catalog-tile-name" to={`/assets/${asset.id}`}>{asset.name}</Link>
      <p className="catalog-tile-tagline">{asset.tagline || 'No tagline yet'}</p>
      <p className="hint">Industries: {tagText(asset.industries)}</p>
      <p className="hint">{confirmedText(asset.last_confirmed_at)}</p>
    </li>
  );
}

export function CatalogTiles({ state, rows }: { state: CatalogPageState; rows: AssetCard[] }) {
  return (
    <ul className="catalog-grid" aria-label="Assets">
      {rows.map((asset) => (
        <AssetTile key={asset.id} asset={asset} drafts={state.canDrafts} />
      ))}
    </ul>
  );
}
